/**
 * Barra laterale di navigazione — wireframe §6 del documento di progettazione.
 *
 * Chiara, con l'ente in cima e l'utenza in fondo, come nel back-office gemello:
 * il pedice porta anche il selettore del tema e l'uscita, che prima stavano
 * nella testata.
 *
 * Le voci del gruppo «Amministrazione» compaiono al solo super admin. La
 * differenza fra i due ruoli e' di **visibilita' delle sezioni, non di layout**:
 * il redattore vede la stessa applicazione con meno voci.
 *
 * Va detto una volta e ricordato: questa e' interfaccia, non autorizzazione.
 * Il vincolo di esclusivita' sulle categorie e sulla configurazione vive negli
 * endpoint dell'API; qui lo si rispecchia per non proporre all'operatore strade
 * che finirebbero in un 403.
 */

import { NavLink, useNavigate } from "react-router-dom";
import { useAutenticazione } from "../auth/contesto";
import { useAlberatura } from "../dati/contesto-alberatura";
import {
  IconaAlberatura,
  IconaConformita,
  IconaCruscotto,
  IconaDocumento,
  IconaEnti,
  IconaEsci,
  IconaProfilo,
  IconaPubblicazioni,
  IconaScadenzario,
  IconaSchemi,
  IconaUtenti,
} from "../components/Icone";
import { SelettoreTema } from "../tema/FornitoreTema";
import logoAstra from "../assets/logo-astra.png";

interface VoceMenu {
  percorso: string;
  etichetta: string;
  Icona: typeof IconaCruscotto;
  /** Vero se la voce deve essere evidenziata solo sul percorso esatto. */
  esatta?: boolean;
}

/** Voci comuni a entrambi i ruoli. */
const VOCI_REDAZIONE: VoceMenu[] = [
  { percorso: "/", etichetta: "Cruscotto", Icona: IconaCruscotto, esatta: true },
  { percorso: "/pubblicazioni", etichetta: "Pubblicazioni", Icona: IconaPubblicazioni },
  { percorso: "/documenti", etichetta: "Documenti e cartelle", Icona: IconaDocumento },
  { percorso: "/scadenzario", etichetta: "Scadenze e allerte", Icona: IconaScadenzario },
  {
    percorso: "/accessibilita",
    etichetta: "Accessibilita' dei documenti",
    Icona: IconaDocumento,
  },
];

/**
 * Voci riservate al super admin (⬩ nel wireframe).
 *
 * «Alberatura / Categorie» non e' piu' la sola configurazione della struttura:
 * e' anche il posto da cui si pubblica cio' che e' stato caricato. Resta
 * riservata, perche' riservate sono le operazioni sull'albero che vi convivono.
 */
const VOCI_AMMINISTRAZIONE: VoceMenu[] = [
  { percorso: "/alberatura", etichetta: "Alberatura / Categorie", Icona: IconaAlberatura },
  { percorso: "/schemi", etichetta: "Schemi ANAC", Icona: IconaSchemi },
  { percorso: "/utenti", etichetta: "Utenti e assegnazioni", Icona: IconaUtenti },
  { percorso: "/enti", etichetta: "Enti", Icona: IconaEnti },
  { percorso: "/conformita", etichetta: "at_map e conformita'", Icona: IconaConformita },
];

function Voce({ percorso, etichetta, Icona, esatta }: VoceMenu) {
  return (
    <NavLink
      to={percorso}
      end={esatta}
      className={({ isActive }) => `barra__voce${isActive ? " barra__voce--attiva" : ""}`}
    >
      <Icona className="barra__icona" />
      <span>{etichetta}</span>
    </NavLink>
  );
}

/** Prime due lettere del nome utente, per l'avatar. */
function iniziali(nomeUtente: string): string {
  return nomeUtente.slice(0, 2).toUpperCase();
}

export function BarraLaterale() {
  const { eSuperAdmin } = useAutenticazione();
  const { ente } = useAlberatura();

  return (
    <nav className="barra" aria-label="Navigazione principale">
      <div className="barra__marchio">
        <span className="barra__logo">
          <img src={logoAstra} alt="ASTRA — The Intelligence Layer" />
        </span>
        <div className="barra__identita">
          <div className="barra__titolo">Amm. Trasparente</div>
          <div className="barra__ente" title={ente?.dominio ?? undefined}>
            {ente?.denominazione ?? "Back-office"}
          </div>
        </div>
      </div>

      <div className="barra__navigazione">
        <p className="barra__gruppo">Redazione</p>
        {VOCI_REDAZIONE.map((voce) => (
          <Voce key={voce.percorso} {...voce} />
        ))}

        {eSuperAdmin ? (
          <>
            <p className="barra__gruppo">Amministrazione</p>
            {VOCI_AMMINISTRAZIONE.map((voce) => (
              <Voce key={voce.percorso} {...voce} />
            ))}
          </>
        ) : null}

        <p className="barra__gruppo">Account</p>
        <Voce percorso="/profilo" etichetta="Il mio profilo" Icona={IconaProfilo} />
      </div>

      <PediceUtente />
    </nav>
  );
}

/** Utenza corrente, selettore del tema e uscita dalla sessione. */
function PediceUtente() {
  const { utente, eSuperAdmin, esci } = useAutenticazione();
  const naviga = useNavigate();
  if (!utente) return null;

  return (
    <div className="barra__pedice">
      <span className="barra__avatar" aria-hidden="true">
        {iniziali(utente.nome_utente)}
      </span>
      <div className="barra__utente">
        <div className="barra__nome">{utente.nome_utente}</div>
        <div className="barra__ruolo">{eSuperAdmin ? "Super Admin" : "Redattore"}</div>
      </div>
      <div className="barra__comandi">
        <SelettoreTema />
        <button
          type="button"
          className="bottone bottone--fantasma bottone--piccolo bottone--icona"
          onClick={() => {
            esci();
            naviga("/accesso", { replace: true });
          }}
          aria-label="Esci dalla sessione"
          title="Esci dalla sessione"
        >
          <IconaEsci />
        </button>
      </div>
    </div>
  );
}
